// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: a long paste in the composer (features/pasted_text). The text holds the marker line
// `[Pasted text: pasted-text-1.txt]`; the editor shows it as the folded block. The file itself lives
// with the composer's attachments, found through ComposerPastedTextContext.

import {pastedTextMarker} from '@app/features/pasted_text/PastedText';
import {PastedTextFold, type PastedTextSource} from '@app/features/pasted_text/PastedTextFold';
import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {
	$getNodeByKey,
	$isLineBreakNode,
	DecoratorNode,
	type DOMExportOutput,
	type LexicalNode,
	type NodeKey,
	type SerializedLexicalNode,
	type Spread,
} from 'lexical';
import {createContext, type JSX, useContext} from 'react';

/** The composer's pasted files, by name. Without a provider a fold shows without its peek. */
export const ComposerPastedTextContext = createContext<{source: (name: string) => PastedTextSource | null} | null>(
	null,
);

export type SerializedComposerPastedTextNode = Spread<{name: string}, SerializedLexicalNode>;

function ComposerPastedText({name, nodeKey}: {name: string; nodeKey: NodeKey}) {
	const [editor] = useLexicalComposerContext();
	const files = useContext(ComposerPastedTextContext);
	return (
		<PastedTextFold
			source={files?.source(name) ?? null}
			onRemove={() =>
				editor.update(() => {
					const node = $getNodeByKey(nodeKey);
					if (!node) return;
					// The line break the paste was put on goes with it, so no empty line is left behind.
					const next = node.getNextSibling();
					if ($isLineBreakNode(next)) next.remove();
					else if ($isLineBreakNode(node.getPreviousSibling())) node.getPreviousSibling()?.remove();
					node.remove();
				})
			}
			flx="lexical.composer.pasted-text"
		/>
	);
}

export class ComposerPastedTextNode extends DecoratorNode<JSX.Element> {
	__name: string;

	static override getType(): string {
		return 'composer-pasted-text';
	}

	static override clone(node: ComposerPastedTextNode): ComposerPastedTextNode {
		return new ComposerPastedTextNode(node.__name, node.__key);
	}

	static override importJSON(serializedNode: SerializedComposerPastedTextNode): ComposerPastedTextNode {
		return $createComposerPastedTextNode(serializedNode.name);
	}

	constructor(name: string, key?: NodeKey) {
		super(key);
		this.__name = name;
	}

	override exportJSON(): SerializedComposerPastedTextNode {
		return {...super.exportJSON(), name: this.__name};
	}

	override createDOM(): HTMLElement {
		const element = document.createElement('span');
		element.style.display = 'block';
		element.setAttribute('data-lexical-pasted-text', this.__name);
		return element;
	}

	override updateDOM(): boolean {
		return false;
	}

	override exportDOM(): DOMExportOutput {
		const element = document.createElement('span');
		element.textContent = this.getTextContent();
		return {element};
	}

	override getTextContent(): string {
		return pastedTextMarker(this.getLatest().__name);
	}

	getName(): string {
		return this.getLatest().__name;
	}

	override isInline(): true {
		return true;
	}

	override isKeyboardSelectable(): false {
		return false;
	}

	override decorate(): JSX.Element {
		return <ComposerPastedText name={this.__name} nodeKey={this.__key} />;
	}
}

export function $createComposerPastedTextNode(name: string): ComposerPastedTextNode {
	return new ComposerPastedTextNode(name);
}

export function $isComposerPastedTextNode(node: LexicalNode | null | undefined): node is ComposerPastedTextNode {
	return node instanceof ComposerPastedTextNode;
}
